import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useSearchParams,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { Amplify } from "aws-amplify";
import { getCurrentUser, signOut, fetchAuthSession } from "@aws-amplify/auth";
import awsConfig from "./config/aws-config";
import { Loader2 } from "lucide-react";
import { AuthProvider, AuthProviderWithRouterWrapper } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { PollingProvider } from './context/PollingContext';
import { CompetitionProvider } from './pages/competition/CompetitionContext';
import { ImageOptimizationProvider } from './context/ImageOptimizationContext';
import { RegistrationRulesProvider } from './contexts/RegistrationRulesContext';

// Layout Components
import Sidebar from "./components/sidebar/Sidebar";

// Public Pages
import HomePage from "./components/HomePage";
import AuthSystem from "./components/AuthSystem";

// User Pages
import UserDashboard from "./pages/user/UserDashboard";
import EventsPage from "./pages/user/EventsPage";
import RoutinesPage from "./pages/user/RoutinesPage";
import StudioPage from "./pages/user/StudioPage";
import CritiquesPage from "./pages/user/CritiquesPage";
import UserSettings from "./pages/user/UserSettings";
import RegistrationFlow from "./pages/user/registration/RegistrationFlow.tsx";



// Admin Pages
import AdminDashboard from "./pages/AdminDashboard";
import LimitedAdminDashboard from "./pages/admin/LimitedAdminDashboard";
import EventsManager from "./pages/admin/EventsManager";
import StudiosManager from "./pages/admin/StudiosManager";
import AdminSettings from "./pages/admin/AdminSettings";
import ScholarshipsManager from "./pages/admin/ScholarshipsManager";
import CompetitionManager from "./pages/admin/CompetitionManager";
import StagesManager from "./pages/admin/StagesManager";

// Awards Visualizer
import AwardsVisualizerPage from "./pages/AwardsVisualizerPage";
import TabulatorPage from "./pages/TabulatorPage";
import JudgeStationPage from "./pages/JudgeStationPage";

import EmceePage from "./pages/EmceePage";
import UnauthorizedPage from "./pages/UnauthorizedPage";

// Marketing Pages
import MarketingHub from "./pages/admin/MarketingHub";
import LandingPage from "./pages/admin/marketing/LandingPage";
import EmailBroadcasts from "./pages/admin/marketing/EmailBroadcasts";
import EmailAutomation from "./pages/admin/marketing/EmailAutomation";

// Competition Portal Pages
import CompetitionPortalHome from "./pages/competition/CompetitionPortalHome";
import CompetitionCreation from "./pages/competition/CompetitionCreation";
import JudgeSelection from "./pages/competition/JudgeSelection";

// Admin Settings Pages
import RegistrationSettings from "./pages/admin/RegistrationSettings";
import CompetitionSettings from "./pages/admin/CompetitionSettings";
import TabulatorSettings from "./pages/admin/TabulatorSettings";
import PaymentSettings from "./pages/admin/settings/PaymentSettings";
import CompanySettingsPage from "./pages/admin/settings/CompanySettings";
import ConventionSubdomainSettings from "./pages/admin/settings/ConventionSubdomainSettings";
import UsersSettingsPage from "./pages/admin/settings/UsersSettings";
import SeasonEventSettingsPage from "./pages/admin/settings/SeasonEventSettings";
import RegistrationRulesSettingsPage from "./pages/admin/settings/RegistrationRulesSettings";
import SchedulingSettingsPage from "./pages/admin/settings/SchedulingSettings";
import JudgingAwardsSettingsPage from "./pages/admin/settings/JudgingAwardsSettings";
import PaymentFinancialSettingsPage from "./pages/admin/settings/PaymentSettings";
import MediaSettingsPage from "./pages/admin/settings/MediaSettings";
import ReportsSettingsPage from "./pages/admin/settings/ReportsSettings";
import SecuritySettingsPage from "./pages/admin/settings/SecuritySettings";
import PricingSettingsPage from "./pages/admin/settings/PricingSettings";

// Stripe Components
import CheckoutPage from "./components/stripe/CheckoutPage";

// Context Providers
import { JudgeStationSettingsProvider } from "./context/JudgeStationSettingsContext";

// Import CSS variables
import './darkMode.css';

import ConventionAccess from './components/ConventionAccess';
import ConventionLanding from './components/ConventionLanding';
import { isConventionSubdomain, getSubdomain, handleConventionAccess, getMainDomainUrl, redirectToMainDomain, hasConventionAccess } from './utils/conventionService';

// Configure Amplify
Amplify.configure(awsConfig);

// Convention Subdomain Handler Component
const ConventionSubdomainHandler = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        const user = await getCurrentUser();
        setIsAuthenticated(true);
        
        // For authenticated users, process convention access and redirect to main domain
        const subdomain = getSubdomain();
        if (subdomain) {
          try {
            await handleConventionAccess();
            console.log(`Convention access granted for: ${subdomain}`);
          } catch (error) {
            console.error('Error granting convention access:', error);
            // Continue with redirect even if convention access fails
          }
        }
        
        // Redirect authenticated users to main domain dashboard
        redirectToMainDomain('/user/dashboard');
        
      } catch (error) {
        // User is not authenticated - show the convention landing page
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndRedirect();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-pink-400" />
        <p className="ml-4 text-white/70">
          Loading...
        </p>
      </div>
    );
  }

  if (isAuthenticated) {
    // Show loading while redirecting authenticated users
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-pink-400" />
        <p className="ml-4 text-white/70">
          Redirecting to dashboard...
        </p>
      </div>
    );
  }

  // Show ConventionLanding for unauthenticated users
  return <ConventionLanding />;
};

const AuthWrapper = () => {
  const [searchParams] = useSearchParams();
  const initialState = searchParams.get("state") || "signIn";
  return <AuthSystem initialAuthState={initialState} />;
};

function AdminLayout() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const { theme } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;
        const payload = accessToken?.payload;
        
        // Check if we have a valid payload before accessing cognito:groups
        if (!payload || !accessToken) {
          throw new Error("No valid authentication session found");
        }
        
        const groups = payload["cognito:groups"] || [];
        setUserGroups(groups);

        // Allow Admin group full access, Judge group only settings access
        if (groups.includes("Admin")) {
          setIsAuthenticated(true);
        } else if (groups.includes("Judge")) {
          // Check if judge is trying to access allowed routes
          const currentPath = window.location.pathname;
          
          const isLimitedDashboard = currentPath === '/admin/limited-dashboard';
          const isSettingsPath = currentPath.startsWith('/admin/settings');
          const isCompetitionPath = currentPath.startsWith('/admin/competition');
          const isStagesPath = currentPath.startsWith('/admin/stages');
          
          if (isLimitedDashboard || isSettingsPath || isCompetitionPath || isStagesPath) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            navigate("/admin/limited-dashboard");
          }
        } else {
          setIsAuthenticated(false);
          navigate("/user/dashboard");
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/auth?state=signIn");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      // Clear auth state immediately to prevent redirect loops
      localStorage.removeItem('auth_user');
      setIsAuthenticated(false);
      setUserGroups([]);
      
      await signOut();
      navigate("/auth?state=signIn");
    } catch (error) {
      console.error("Error signing out:", error);
      // Even if signOut fails, clear local state and redirect
      localStorage.removeItem('auth_user');
      setIsAuthenticated(false);
      setUserGroups([]);
      navigate("/auth?state=signIn");
    }
  };

  const handleSidebarCollapseChange = (isCollapsed) => {
    setIsSidebarCollapsed(isCollapsed);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient-vibrant dark:bg-hero-gradient-vibrant">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth?state=signIn" replace />;
  }

  // Determine the appropriate role for the sidebar
  const getSidebarRole = () => {
    // If user is Admin only, show full admin sidebar
    if (userGroups.includes("Admin") && !userGroups.includes("Judge")) {
      return "admin";
    }
    // If user is Judge (with or without Admin), show limited sidebar
    if (userGroups.includes("Judge")) {
      return "limited-admin";
    }
    // Default to admin for backwards compatibility
    return "admin";
  };

  return (
    <div className={`flex min-h-screen ${theme === 'dark' ? 'bg-hero-gradient-vibrant' : 'bg-base-200'}`} data-theme={theme}>
      <div className={`hidden md:block ${isSidebarCollapsed ? 'w-16' : 'w-64'} shrink-0 transition-all duration-300`}></div>
      <Sidebar
        role={getSidebarRole()}
        onSignOut={handleSignOut}
        onCollapseChange={handleSidebarCollapseChange}
        appName="Dance Digital"
      />
      <main className="flex-1 w-full">
        <div className="h-full p-4 sm:p-6 md:pt-4 md:px-6 md:pb-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function ProtectedUserLayout() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { theme } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/auth?state=signIn");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      // Clear auth state immediately to prevent redirect loops
      localStorage.removeItem('auth_user');
      setIsAuthenticated(false);
      
      await signOut();
      navigate("/auth?state=signIn");
    } catch (error) {
      console.error("Error signing out:", error);
      // Even if signOut fails, clear local state and redirect
      localStorage.removeItem('auth_user');
      setIsAuthenticated(false);
      navigate("/auth?state=signIn");
    }
  };

  const handleSidebarCollapseChange = (isCollapsed) => {
    setIsSidebarCollapsed(isCollapsed);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient-vibrant dark:bg-hero-gradient-vibrant">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth?state=signIn" replace />;
  }

  return (
    <div className={`flex min-h-screen ${theme === 'dark' ? 'bg-hero-gradient-vibrant' : 'bg-base-200'}`} data-theme={theme}>
      <div className={`hidden md:block ${isSidebarCollapsed ? 'w-16' : 'w-64'} shrink-0 transition-all duration-300`}></div>
      <Sidebar
        role="user"
        onSignOut={handleSignOut}
        onCollapseChange={handleSidebarCollapseChange}
        appName="Studio Portal"
      />
      <main className="flex-1 w-full">
        <div className="h-full p-4 sm:p-6 md:pt-4 md:px-6 md:pb-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}



function ProtectedAdminRoute({ children }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;
        const payload = accessToken?.payload;
        
        // Check if we have a valid payload before accessing cognito:groups
        if (!payload || !accessToken) {
          throw new Error("No valid authentication session found");
        }
        
        const groups = payload["cognito:groups"] || [];

        if (groups.includes("Admin")) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate("/user/dashboard");
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/auth?state=signIn");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient-vibrant dark:bg-hero-gradient-vibrant">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth?state=signIn" replace />;
  }

  return children;
}

function ProtectedCompetitionRoute({ children }) {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const maxRetries = 3;
    const retryDelays = [1000, 3000, 5000]; // Progressive delays: 1s, 3s, 5s
    
    const callAccessControlAPIWithRetry = async (competitionId, token, attempt = 0) => {
      try {
        console.log(`📞 Calling Competition Access Control API V2... (Attempt ${attempt + 1}/${maxRetries + 1})`);
        
        const accessResponse = await fetch(`https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/competition/${competitionId}/access-check`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'view',
            requestSource: 'competition-route-guard'
          })
        });

        if (!accessResponse.ok) {
          const errorData = await accessResponse.json();
          
          // Don't retry auth failures (401) or explicit denials (403/404)
          if (accessResponse.status === 401) {
            setIsAuthenticated(false);
            navigate("/auth?state=signIn");
            return null;
          } else if (accessResponse.status === 403 || accessResponse.status === 404) {
            setIsAuthenticated(false);
            navigate("/unauthorized");
            return null;
          } else {
            // For other errors (500, network issues), retry if attempts remain
            throw new Error(`HTTP ${accessResponse.status}: ${errorData.message || 'API Error'}`);
          }
        }

        const accessResult = await accessResponse.json();
        console.log('🔐 Access Control V2 Result:', accessResult);
        return accessResult;
        
      } catch (error) {
        console.error(`❌ Access Control API attempt ${attempt + 1} failed:`, error);
        
        // If we have retries left and this is a network/server error, retry
        if (attempt < maxRetries) {
          const delay = retryDelays[attempt] || 5000;
          console.log(`🔄 Retrying in ${delay}ms... (${maxRetries - attempt} attempts remaining)`);
          setRetryAttempts(attempt + 1);
          
          await new Promise(resolve => setTimeout(resolve, delay));
          return callAccessControlAPIWithRetry(competitionId, token, attempt + 1);
        }
        
        // All retries exhausted
        console.error('🚫 SECURITY: All access control API retries failed - denying access for security');
        throw error;
      }
    };

    const checkAuthAndAccess = async () => {
      try {
        // Check if we have a recent valid access cache for this competition
        const currentCompetitionId = params.competitionCode;
        if (currentCompetitionId) {
          const cacheKey = `competitionAccess_${currentCompetitionId}`;
          const cachedAccess = sessionStorage.getItem(cacheKey);
          
          if (cachedAccess) {
            try {
              const accessData = JSON.parse(cachedAccess);
              const cacheAge = Date.now() - new Date(accessData.lastChecked).getTime();
              const maxCacheAge = 5 * 60 * 1000; // 5 minutes
              
              if (cacheAge < maxCacheAge && accessData.hasAccess) {
                console.log('✅ Using cached access result (valid for', Math.round((maxCacheAge - cacheAge) / 1000), 'more seconds)');
                setIsAuthenticated(true);
                setRetryAttempts(0);
                return;
              } else {
                console.log('🗑️ Access cache expired or invalid, performing fresh check');
                sessionStorage.removeItem(cacheKey);
              }
            } catch (e) {
              console.warn('Invalid access cache data, removing:', e);
              sessionStorage.removeItem(cacheKey);
            }
          }
        }
        
        // Step 1: Check authentication
        const session = await fetchAuthSession();
        const accessToken = session.tokens?.accessToken;
        const payload = accessToken?.payload;
        
        if (!payload || !accessToken) {
          throw new Error("No valid authentication session found");
        }
        
        const groups = payload["cognito:groups"] || [];
        
        // Get user's actual role from database via AuthContext
        let userRole = null;
        try {
          const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
          userRole = authUser.role;
        } catch (e) {
          console.warn('Could not get user role from localStorage');
        }
        
        // Check for role/group inconsistencies and determine legitimate access
        const hasLegitimateAdminAccess = userRole === 'Admin' || 
          (groups.includes('Admin') && userRole !== 'teacher');
          
        const hasLegitimateJudgeAccess = userRole === 'Judge' || 
          (groups.includes('Judge') && userRole !== 'teacher');
        
        // Log inconsistencies for monitoring
        if (userRole === 'teacher' && groups.includes('Judge')) {
          console.warn('🚨 ROLE INCONSISTENCY: User has database role "teacher" but Cognito group "Judge"');
          console.warn('🔧 RECOMMENDATION: Remove Judge group from Cognito or update database role');
        }
        
        if (!hasLegitimateAdminAccess && !hasLegitimateJudgeAccess) {
          console.log('🚫 ACCESS DENIED: User role:', userRole, 'Cognito groups:', groups);
          console.log('📋 ACCESS REASON: No legitimate Admin or Judge access');
          setIsAuthenticated(false);
          navigate("/user/dashboard");
          return;
        }
        
        const accessType = hasLegitimateAdminAccess ? 'Admin' : 'Judge';
        console.log('✅ ACCESS GRANTED:', accessType, 'access - User role:', userRole, 'Cognito groups:', groups);

        // Step 2: Enhanced security check with access token support
        const competitionId = params.competitionCode || params.competitionId;
        
        if (competitionId) {
          console.log('🔒 SECURITY: Checking access to secure competition:', competitionId);
          console.log('👤 User role:', groups.includes("Admin") ? "Admin" : "Judge");
          
          const token = session.tokens.idToken.toString();
          const urlParams = new URLSearchParams(location.search);
          const accessToken = urlParams.get('token');
          
          // For judge routes with access tokens, verify the token first
          if (params.judgeId && params.position && accessToken) {
            console.log('🔐 SECURITY: Verifying judge access token...');
            
            try {
              const tokenVerifyResponse = await fetch(`https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev/competition/${competitionId}/verify-access`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  judgeId: params.judgeId,
                  position: params.position,
                  token: accessToken,
                  requestSource: 'judge-route-guard'
                })
              });

              if (!tokenVerifyResponse.ok) {
                console.log('🚫 SECURITY: Judge access token verification failed');
                setIsAuthenticated(false);
                navigate("/unauthorized");
                return;
              }

              const tokenResult = await tokenVerifyResponse.json();
              console.log('✅ SECURITY: Judge access token verified successfully');
              
              // Access granted via secure token
              setIsAuthenticated(true);
              setIsLoading(false);
              return;
              
            } catch (tokenError) {
              console.error('🚫 SECURITY: Token verification error:', tokenError);
              setIsAuthenticated(false);
              navigate("/unauthorized");
              return;
            }
          }
          
          // Fall back to standard access control check
          
          try {
            const accessResult = await callAccessControlAPIWithRetry(competitionId, token);
            
            // If accessResult is null, it means we already handled the redirect
            if (!accessResult) {
              return;
            }

            if (!accessResult.hasAccess) {
              console.log('🚫 ACCESS DENIED:', accessResult.reason);
              console.log('👤 User organizerId:', accessResult.userOrganizerId);
              console.log('🏢 Competition organizerId:', accessResult.competitionOrganizerId);
              console.log('🔍 Is organization owner:', accessResult.isOrganizationOwner);
              
              setIsAuthenticated(false);
              navigate("/unauthorized");
              return;
            }

            console.log('✅ ACCESS GRANTED:', accessResult.reason);
            console.log('👤 User organizerId:', accessResult.userOrganizerId);
            console.log('🏢 Competition organizerId:', accessResult.competitionOrganizerId);
            console.log('🔍 Is organization owner:', accessResult.isOrganizationOwner);
            console.log('⚖️ Is assigned judge:', accessResult.isAssignedJudge);
            console.log('🛡️ User permissions:', accessResult.permissions);
            
            // Store access result in sessionStorage for use by components
            const accessData = {
              competitionId,
              hasAccess: true,
              permissions: accessResult.permissions,
              userRole: accessResult.isOrganizationOwner ? 'Owner' : 'Judge',
              lastChecked: new Date().toISOString()
            };
            
            // Store with both old and new cache keys for compatibility
            sessionStorage.setItem('competitionAccess', JSON.stringify(accessData));
            sessionStorage.setItem(`competitionAccess_${competitionId}`, JSON.stringify(accessData));
            
            // Clean up old cache entries to prevent memory buildup
            const cleanupOldCaches = () => {
              const keys = Object.keys(sessionStorage);
              const cacheKeys = keys.filter(key => key.startsWith('competitionAccess_'));
              const maxCacheEntries = 10;
              
              if (cacheKeys.length > maxCacheEntries) {
                // Sort by last accessed time and remove oldest entries
                const cacheEntries = cacheKeys.map(key => {
                  try {
                    const data = JSON.parse(sessionStorage.getItem(key) || '{}');
                    return { key, lastChecked: new Date(data.lastChecked || 0).getTime() };
                  } catch {
                    return { key, lastChecked: 0 };
                  }
                }).sort((a, b) => a.lastChecked - b.lastChecked);
                
                const toRemove = cacheEntries.slice(0, cacheEntries.length - maxCacheEntries);
                toRemove.forEach(entry => {
                  console.log('🗑️ Cleaning up old access cache:', entry.key);
                  sessionStorage.removeItem(entry.key);
                });
              }
            };
            
            cleanupOldCaches();

          } catch (error) {
            console.error('❌ Error calling Access Control API V2 (all retries failed):', error);
            console.log('🚫 SECURITY: Access check failed after all retries - denying access for security');
            setIsAuthenticated(false);
            navigate("/unauthorized");
            return;
          }
        }

        // All checks passed
        setIsAuthenticated(true);
        setRetryAttempts(0); // Reset retry attempts on success
        
      } catch (error) {
        console.error("Authentication/Authorization check failed:", error);
        setIsAuthenticated(false);
        navigate("/auth?state=signIn");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndAccess();
  }, [navigate, params, retryAttempts]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient-vibrant dark:bg-hero-gradient-vibrant">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-white">Verifying access...</p>
          {retryAttempts > 0 && (
            <p className="text-white/70 text-sm mt-2">
              Retrying connection... (Attempt {retryAttempts + 1})
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth?state=signIn" replace />;
  }

  return children;
}

// Main App Content Based on Domain
function DomainRouter() {
  // Check if we're on a convention subdomain
  const isConvention = isConventionSubdomain();
  
  return (
    <ThemeProvider>
      <JudgeStationSettingsProvider>
        <PollingProvider>
          <CompetitionProvider>
          {/* Show convention access page if on a subdomain */}
          {isConvention ? (
            <Routes>
              {/* Auth routes available on subdomains */}
              <Route path="/auth" element={<AuthWrapper />} />
              
              {/* Convention-specific routes */}
              <Route path="/" element={<ConventionSubdomainHandler />} />
              
              {/* Protected User Routes - allow access from subdomains */}
              <Route path="/user" element={
                <AuthProviderWithRouterWrapper>
                  <ProtectedUserLayout />
                </AuthProviderWithRouterWrapper>
              }>
                <Route path="dashboard" element={<UserDashboard />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="registration/*" element={<RegistrationFlow />} />
                <Route path="routines" element={<RoutinesPage />} />
                <Route path="studio" element={<StudioPage />} />
                <Route path="critiques" element={<CritiquesPage />} />
                <Route path="settings" element={<UserSettings />} />
              </Route>
              
              {/* Catch-all route for subdomains */}
              <Route path="*" element={<ConventionSubdomainHandler />} />
            </Routes>
          ) : (
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/auth" element={<AuthWrapper />} />
              <Route path="/pricing" element={<h1>Pricing Page Coming Soon!</h1>} />
              <Route path="/schedule" element={<h1>Schedule Page Coming Soon!</h1>} />
              <Route path="/criteria" element={<h1>Criteria Page Coming Soon!</h1>} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              
              {/* Awards Visualizer - Authenticated Route (requires sign-in + tabulator password) */}
              <Route path="/awards-visualizer/:competitionCode" element={
                <AuthProviderWithRouterWrapper>
                  <AwardsVisualizerPage />
                </AuthProviderWithRouterWrapper>
              } />
              
              {/* Competition Routes - Require Authentication */}
              <Route path="/competition/portal" element={
                <AuthProviderWithRouterWrapper>
                  <ProtectedCompetitionRoute>
                    <CompetitionPortalHome />
                  </ProtectedCompetitionRoute>
                </AuthProviderWithRouterWrapper>
              } />
              <Route path="/competition/setup" element={
                <AuthProviderWithRouterWrapper>
                  <ProtectedCompetitionRoute>
                    <CompetitionCreation />
                  </ProtectedCompetitionRoute>
                </AuthProviderWithRouterWrapper>
              } />
              <Route path="/competition/join" element={
                <AuthProviderWithRouterWrapper>
                  <ProtectedCompetitionRoute>
                    <JudgeSelection />
                  </ProtectedCompetitionRoute>
                </AuthProviderWithRouterWrapper>
              } />
              
              {/* Protected Competition Routes - Require Authentication */}
              <Route path="/competition/tabulator/:competitionId" element={
                <AuthProviderWithRouterWrapper>
                  <ProtectedCompetitionRoute>
                    <RegistrationRulesProvider>
                      <TabulatorPage />
                    </RegistrationRulesProvider>
                  </ProtectedCompetitionRoute>
                </AuthProviderWithRouterWrapper>
              } />
              <Route path="/competition/judge/:competitionId/:judgeId" element={
                <AuthProviderWithRouterWrapper>
                  <ProtectedCompetitionRoute>
                    <RegistrationRulesProvider>
                      <JudgeStationPage />
                    </RegistrationRulesProvider>
                  </ProtectedCompetitionRoute>
                </AuthProviderWithRouterWrapper>
              } />
              
              {/* Protected User Routes */}
              <Route path="/user" element={
                <AuthProviderWithRouterWrapper>
                  <ProtectedUserLayout />
                </AuthProviderWithRouterWrapper>
              }>
                <Route path="dashboard" element={<UserDashboard />} />
                <Route path="events" element={<EventsPage />} />
                <Route path="registration/*" element={<RegistrationFlow />} />
                <Route path="routines" element={<RoutinesPage />} />
                <Route path="studio" element={<StudioPage />} />
                <Route path="critiques" element={<CritiquesPage />} />
                <Route path="settings" element={<UserSettings />} />
              </Route>
              
              {/* Protected Admin Routes */}
              <Route path="/admin" element={
                <AuthProviderWithRouterWrapper>
                  <AdminLayout />
                </AuthProviderWithRouterWrapper>
              }>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="limited-dashboard" element={<LimitedAdminDashboard />} />
                <Route path="events" element={<EventsManager />} />
                <Route path="studios" element={<StudiosManager />} />
                <Route path="scholarships" element={<ScholarshipsManager />} />
                <Route path="competition" element={<CompetitionManager />} />
                <Route path="stages" element={<StagesManager />} />
                
                {/* Marketing routes */}
                <Route path="marketing" element={<MarketingHub />} />
                <Route path="marketing/landing-page" element={<LandingPage />} />
                <Route path="marketing/email-broadcasts" element={<EmailBroadcasts />} />
                <Route path="marketing/automation" element={<EmailAutomation />} />
                <Route path="marketing/notifications" element={<Navigate to="/admin/marketing" replace />} />
                
                {/* Settings routes */}
                <Route path="settings" element={<AdminSettings />} />
                <Route path="settings/registration" element={<RegistrationSettings />} />
                <Route path="settings/competition" element={<CompetitionSettings />} />
                <Route path="settings/tabulator" element={<TabulatorSettings />} />
                <Route path="settings/payment" element={<PaymentSettings />} />
                
                {/* New settings routes */}
                <Route path="settings/company" element={<CompanySettingsPage />} />
                <Route path="settings/convention-subdomain" element={<ConventionSubdomainSettings />} />
                <Route path="settings/users" element={<UsersSettingsPage />} />
                <Route path="settings/season-events" element={<SeasonEventSettingsPage />} />
                <Route path="settings/registration-rules/*" element={<RegistrationRulesSettingsPage />} />
                <Route path="settings/scheduling" element={<SchedulingSettingsPage />} />
                <Route path="settings/judging-awards" element={<JudgingAwardsSettingsPage />} />
                <Route path="settings/judges" element={<JudgingAwardsSettingsPage />} />
                <Route path="settings/payment-financial/" element={<PaymentFinancialSettingsPage />} />
                <Route path="settings/media" element={<MediaSettingsPage />} />
                <Route path="settings/reports" element={<ReportsSettingsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/pricing/*" element={<PricingSettingsPage />} />
                
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
              </Route>
              
              {/* Emcee Route */}
              <Route
                path="/emcee/:competitionId"
                element={
                  <AuthProviderWithRouterWrapper>
                    <ProtectedCompetitionRoute>
                      <EmceePage />
                    </ProtectedCompetitionRoute>
                  </AuthProviderWithRouterWrapper>
                }
              />
              
              {/* Competition-based Routes - PROTECTED */}
              <Route
                path="/competition/:competitionCode/tabulator"
                element={
                  <AuthProviderWithRouterWrapper>
                    <ProtectedCompetitionRoute>
                      <RegistrationRulesProvider>
                        <TabulatorPage />
                      </RegistrationRulesProvider>
                    </ProtectedCompetitionRoute>
                  </AuthProviderWithRouterWrapper>
                }
              />
              
              <Route
                path="/competition/:competitionCode/judge/:judgeId/:position"
                element={
                  <AuthProviderWithRouterWrapper>
                    <ProtectedCompetitionRoute>
                      <RegistrationRulesProvider>
                        <JudgeStationPage />
                      </RegistrationRulesProvider>
                    </ProtectedCompetitionRoute>
                  </AuthProviderWithRouterWrapper>
                }
              />
              
              {/* Legacy Routes - Redirect to Competition Portal */}
              <Route
                path="/tabulator"
                element={<Navigate to="/competition/portal" replace />}
              />
              
              <Route
                path="/judge-station/:judgeId"
                element={<Navigate to="/competition/portal" replace />}
              />
              

              
              {/* Catch-all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
          </CompetitionProvider>
        </PollingProvider>
      </JudgeStationSettingsProvider>
    </ThemeProvider>
  );
}

// Root App Component with Theme Provider
function App() {
  return (
    <AuthProvider>
      <ImageOptimizationProvider>
        <Router>
          <DomainRouter />
        </Router>
      </ImageOptimizationProvider>
    </AuthProvider>
  );
}

export default App;